import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import FormButton from "../../../components/form-components/FormButton";
import Toggle from "../../../components/ui-components/Toggle";
import StatusPill from "./StatusPill";
import ThresholdBar from "../../budget-enforcement/components/ThresholdBar";
import {
	formatMoney,
	sumAllocated,
	distributeEven,
	ALLOCATION_STRATEGY_OPTIONS,
} from "../utils/allocationUtils";
import { MoneyInput } from "../../../components/form-components/MoneyInput";
import FilterDropdown from "../../../components/ui-components/FilterDropdown";
import { forecastTitle } from "../utils/periodForecast";
import {
	periodFinancialState,
	utilizationTone,
	formatPeriodRange,
} from "../utils/budgetOutlook";
import {
	Panel,
	PanelSection,
	PANEL_EYEBROW,
	PANEL_SUPPORT,
} from "./Panel";

const PERIOD_TYPE_OPTIONS = [
	{ value: "monthly", label: "Monthly" },
	{ value: "quarterly", label: "Quarterly" },
];

const CURRENCY_OPTIONS = [
	{ value: "EUR", label: "EUR" },
	{ value: "USD", label: "USD" },
	{ value: "GBP", label: "GBP" },
];

const VIEW_FILTER_OPTIONS = [
	{ value: "all", label: "All" },
	{ value: "fy", label: "FY" },
	{ value: "next_6", label: "Next 6 months" },
];

// Start/end date for a month (YYYY-MM)
const monthStartEnd = (y, m) => {
	const start = new Date(y, m - 1, 1);
	const end = new Date(y, m, 0);
	return {
		startDate: start.toISOString().slice(0, 10),
		endDate: end.toISOString().slice(0, 10),
	};
};

// Start/end for a quarter (1-based)
const quarterStartEnd = (y, q) => {
	const startM = (q - 1) * 3 + 1;
	const endM = q * 3;
	const start = new Date(y, startM - 1, 1);
	const end = new Date(y, endM, 0);
	return {
		startDate: start.toISOString().slice(0, 10),
		endDate: end.toISOString().slice(0, 10),
	};
};

// Generate period keys with id, label, periodKey, startDate, endDate, amountCents
const generatePeriodKeys = (
	periodType,
	startMonth,
	count,
	totalAmountCents = 0,
) => {
	const periods = [];
	const [year, month] = startMonth.split("-").map(Number);
	const date = new Date(year, month - 1, 1);

	for (let i = 0; i < count; i++) {
		if (periodType === "monthly") {
			const y = date.getFullYear();
			const m = date.getMonth() + 1;
			const mStr = String(m).padStart(2, "0");
			const periodKey = `${y}-${mStr}`;
			const { startDate, endDate } = monthStartEnd(y, m);
			periods.push({
				id: periodKey,
				label: periodKey,
				periodKey,
				startDate,
				endDate,
				amountCents: 0,
				amount: 0,
				status: "draft",
				forecast: null,
			});
			date.setMonth(date.getMonth() + 1);
		} else {
			const y = date.getFullYear();
			const q = Math.ceil((date.getMonth() + 1) / 3);
			const periodKey = `${y}-Q${q}`;
			const { startDate, endDate } = quarterStartEnd(y, q);
			periods.push({
				id: periodKey,
				label: periodKey,
				periodKey,
				startDate,
				endDate,
				amountCents: 0,
				amount: 0,
				status: "draft",
				forecast: null,
			});
			date.setMonth(date.getMonth() + 3);
		}
	}
	if (totalAmountCents > 0 && periods.length > 0) {
		const amounts = distributeEven(totalAmountCents, periods.length);
		periods.forEach((p, i) => {
			p.amountCents = amounts[i] ?? 0;
			p.amount = (amounts[i] ?? 0) / 100;
		});
	}
	return periods;
};

// Get current month as YYYY-MM default
const getCurrentMonth = () => {
	const now = new Date();
	const y = now.getFullYear();
	const m = String(now.getMonth() + 1).padStart(2, "0");
	return `${y}-${m}`;
};

const BudgetPeriodsPanel = ({
	periods,
	onPeriodsChange,
	periodType: budgetPeriodType,
	totalAmountCents = 0,
	allocationStrategy = "even",
	onTotalChange,
	onStrategyChange,
	currency = "EUR",
	onCurrencyChange,
	regeneratePeriodAmounts,
	onEditPeriod,
}) => {
	const hasPeriods = periods.length > 0;
	const allocated = sumAllocated(periods);
	const isOverAllocated = totalAmountCents > 0 && allocated > totalAmountCents;
	const isUnderAllocated = totalAmountCents > 0 && allocated < totalAmountCents;
	const showTableWhenReady = hasPeriods;
	const showTable = showTableWhenReady && totalAmountCents > 0;
	const showEmptyTotalState = hasPeriods && totalAmountCents <= 0;

	// Setup state for pre-generation card
	const [setupPeriodType, setSetupPeriodType] = useState(
		budgetPeriodType === "quarterly" ? "quarterly" : "monthly",
	);
	const [startMonth, setStartMonth] = useState(getCurrentMonth);
	const [generateCount, setGenerateCount] = useState(12);
	const [isGenerating, setIsGenerating] = useState(false);
	const [tableVisible, setTableVisible] = useState(hasPeriods);

	// When periods are hydrated from the API after initial mount, flip to table view
	useEffect(() => {
		if (hasPeriods && !tableVisible) {
			setTableVisible(true);
		}
	}, [hasPeriods]); // eslint-disable-line react-hooks/exhaustive-deps

	// View filter
	const [viewFilter, setViewFilter] = useState("all");

	// Brief success pulse on reconciliation bar when status becomes balanced
	const [reconciliationPulse, setReconciliationPulse] = useState(false);
	const prevBalancedRef = useRef(false);
	useEffect(() => {
		const balanced = totalAmountCents > 0 && allocated === totalAmountCents;
		if (balanced && !prevBalancedRef.current) {
			setReconciliationPulse(true);
			const t = setTimeout(() => setReconciliationPulse(false), 300);
			return () => clearTimeout(t);
		}
		prevBalancedRef.current = !!balanced;
	}, [allocated, totalAmountCents]);

	const handleGenerate = () => {
		setIsGenerating(true);
		setTimeout(() => {
			const newPeriods = generatePeriodKeys(
				setupPeriodType,
				startMonth,
				generateCount,
				totalAmountCents,
			);
			onPeriodsChange(newPeriods);
			setIsGenerating(false);
			setTableVisible(true);
		}, 400);
	};

	const handleRowClick = (period) => {
		if (onEditPeriod) onEditPeriod(period);
	};

	const showSetup = !tableVisible;

	// The span the authorization covers, stated once beside the amount instead of left for
	// the reader to infer from the first and last rows of the table.
	const authorizedRange = formatPeriodRange(periods);

	// The lifecycle status of a row — approval state, not financial state.
	const statusKey = (period) => {
		if (period.derivedStatus) {
			if (period.derivedStatus === "draft_change") return "draft";
			if (period.derivedStatus === "not_set") return "archived";
			if (
				period.derivedStatus === "rejected" &&
				period.active_amount_cents != null
			)
				return "active";
			return period.derivedStatus;
		}
		if (period.status === "breach") return "breach";
		return period.status || "draft";
	};

	// A column in which every row says the same word is not a column, it is a caption
	// repeated N times. It earns its width only when periods actually differ.
	const showStatusColumn = new Set(periods.map(statusKey)).size > 1;

	const gridCols = showStatusColumn
		? "grid-cols-[1.3fr_1.4fr_1.4fr_1.5fr_1.2fr_0.4fr]"
		: "grid-cols-[1.3fr_1.4fr_1.4fr_1.5fr_0.4fr]";

	return (
		<Panel className="flex flex-col min-w-0">
			{/* Authorization — the number this page exists to state, and the two facts that
			    qualify it: the span it covers and how it is spread. It used to sit in a
			    separate card floating above the table, at the same weight as everything else
			    on the page; it is now the top of the one surface that owns the whole
			    authorization. */}
			<PanelSection title="Authorization">
				<div className="flex items-center gap-2">
					<MoneyInput
						id="total-budget-amount"
						value={totalAmountCents != null ? totalAmountCents / 100 : null}
						onChange={(n) =>
							onTotalChange && onTotalChange(n != null ? Math.round(n * 100) : 0)
						}
						currency={currency}
						placeholder="0,00"
						allowNegative={false}
						maxWidth="240px"
						className="flex-none"
						inputClassName="!text-left text-2xl font-semibold leading-tight"
						prefixClassName="text-2xl font-semibold"
					/>
					<FilterDropdown
						ariaLabel="Currency"
						options={CURRENCY_OPTIONS}
						value={currency}
						onChange={(v) => onCurrencyChange?.(v)}
						minWidth={95}
						height={38}
					/>
				</div>
				{authorizedRange && (
					<p className={`${PANEL_SUPPORT} mt-2`}>Authorized {authorizedRange}</p>
				)}

				<div className="mt-4 flex items-center gap-3">
					<span className={PANEL_EYEBROW}>Allocation strategy</span>
					<FilterDropdown
						ariaLabel="Allocation strategy"
						options={ALLOCATION_STRATEGY_OPTIONS}
						value={allocationStrategy}
						onChange={(v) => onStrategyChange && onStrategyChange(v)}
						minWidth={190}
						height={32}
					/>
				</div>
			</PanelSection>

			{/* Period allocation — a reconciliation, not a performance indicator.
			    A bar labelled "112%" reads like utilization ("we are over budget"), when what
			    it actually says is that the period amounts do not add up to the authorized
			    total: a financial control invariant, and a configuration error the reader has
			    to fix before saving. So the money leads, the bar is a thin secondary check,
			    and the remedy is stated in euros. */}
			<PanelSection title="Period allocation">
				<div
					className={reconciliationPulse ? "animate-pulse-scale duration-300" : ""}
					role="status"
					aria-live="polite"
				>
					<p className="text-sm tabular-nums text-text">
						{formatMoney(allocated, currency)} allocated of{" "}
						{formatMoney(totalAmountCents, currency)}
					</p>
					<div className="mt-2">
						<ThresholdBar
							nowPct={
								totalAmountCents > 0 ? (allocated / totalAmountCents) * 100 : 0
							}
							marks={isOverAllocated ? { hard: 100 } : undefined}
							tone={isOverAllocated ? "danger" : isUnderAllocated ? "warning" : "neutral"}
							height={6}
						/>
					</div>
					{isOverAllocated ? (
						<p className="mt-2 text-xs font-medium text-danger">
							{formatMoney(allocated - totalAmountCents, currency)} over-allocated.
							Reduce period allocations by{" "}
							{formatMoney(allocated - totalAmountCents, currency)} before saving.
						</p>
					) : isUnderAllocated ? (
						<p className="mt-2 text-xs text-warning">
							{formatMoney(totalAmountCents - allocated, currency)} of the authorized
							amount is not yet allocated to a period.
						</p>
					) : (
						<p className="mt-2 text-xs text-text-3">
							Period amounts reconcile to the authorized total.
						</p>
					)}
				</div>
			</PanelSection>

			{/* Period setup (pre-generation) */}
			{showSetup && (
				<PanelSection title="Period setup">
					<div className="space-y-4">
						{/* Period Type */}
						<div>
							<span className="block text-xs font-medium text-text-2 mb-1">
								Period type
							</span>
							<Toggle
								items={PERIOD_TYPE_OPTIONS}
								value={setupPeriodType}
								changeHandler={setSetupPeriodType}
								style="buttons"
							/>
						</div>

						{/* Fiscal calendar */}
						<div>
							<label
								htmlFor="fiscal-calendar"
								className="block text-xs font-medium text-text-2 mb-1"
							>
								Fiscal calendar
							</label>
							<input
								id="fiscal-calendar"
								type="text"
								className="w-full p-2 border border-border rounded-ctrl text-sm bg-surface-2 text-text-2"
								value="Default (Jan-Dec)"
								readOnly
							/>
						</div>

						{/* Start month */}
						<div>
							<label
								htmlFor="start-month"
								className="block text-xs font-medium text-text-2 mb-1"
							>
								Start month
							</label>
							<input
								id="start-month"
								type="month"
								className="w-full p-2 border border-border rounded-ctrl text-sm"
								value={startMonth}
								onChange={(e) => setStartMonth(e.target.value)}
							/>
						</div>

						{/* Generate count */}
						<div>
							<label
								htmlFor="generate-count"
								className="block text-xs font-medium text-text-2 mb-1"
							>
								Number of periods
							</label>
							<input
								id="generate-count"
								type="number"
								min={1}
								max={60}
								className="w-full p-2 border border-border rounded-ctrl text-sm"
								value={generateCount}
								onChange={(e) =>
									setGenerateCount(
										Math.max(1, Number.parseInt(e.target.value, 10) || 1),
									)
								}
							/>
						</div>
					</div>

					<div className="mt-6">
						<FormButton
							title={isGenerating ? "Generating..." : "Generate periods"}
							style="primary"
							onClick={handleGenerate}
							disabled={isGenerating}
							className="w-full"
						/>
					</div>
				</PanelSection>
			)}

			{/* Periods (post-generation) */}
			{showTableWhenReady && (
				<PanelSection
					title={`Periods (${periods.length})`}
					className="flex-1 flex flex-col min-h-0"
					action={
						showTable ? (
							<div className="flex items-center gap-2">
								{/* 26px matches the Bulk edit / Copy from period buttons beside it (px-2 py-1
								    text-xs), so the control row keeps one baseline. */}
								<FilterDropdown
									ariaLabel="Filter periods"
									options={VIEW_FILTER_OPTIONS}
									value={viewFilter}
									onChange={setViewFilter}
									minWidth={110}
									height={26}
								/>
								<button
									className="px-2 py-1 text-xs border border-border rounded-ctrl text-text-2 hover:bg-surface-2 transition-colors duration-100"
									title="Bulk edit (placeholder)"
									disabled
								>
									Bulk edit
								</button>
								<button
									className="px-2 py-1 text-xs border border-border rounded-ctrl text-text-2 hover:bg-surface-2 transition-colors duration-100"
									title="Copy from period (placeholder)"
									disabled
								>
									Copy from period
								</button>
							</div>
						) : null
					}
				>
					{showEmptyTotalState && (
						<p className="text-sm text-warning">
							Enter a total amount to allocate across periods.
						</p>
					)}

					{showTable && (
						<div className="flex-1 min-h-0 flex flex-col">
							{/* Header. Utilization, not "forecast vs budget in euros": a reader compares
							    88.8 → 92.2 → 95.9 → 99.3 at a glance, and cannot do the same with
							    "€18 below / €13 below / €6 below". The money stays, one line down,
							    where it answers "how much" once the percentage has answered "where". */}
							<div
								className={`grid ${gridCols} gap-2 px-1 py-2 border-b border-border sticky top-0 z-10 bg-card`}
							>
								<span className={PANEL_EYEBROW}>Period</span>
								<span className={PANEL_EYEBROW}>Authorized</span>
								<span className={PANEL_EYEBROW}>Forecast</span>
								<span className={PANEL_EYEBROW}>Utilization</span>
								{showStatusColumn && <span className={PANEL_EYEBROW}>Status</span>}
								<span className={`${PANEL_EYEBROW} text-right`}>&nbsp;</span>
							</div>

							{/* Rows */}
							<div className="overflow-auto flex-1">
								{periods.map((period) => {
									const periodId = period.id ?? period.periodKey;
									const amountCents =
										Number(period.amountCents) ??
										Math.round((period.amount ?? 0) * 100);
									const isZero =
										period.active_amount_cents != null
											? period.active_amount_cents === 0
											: amountCents === 0;

									// The authorized amount for this period, in units, and the financial
									// state of the projection against it (distinct from the row's
									// lifecycle status beside it, which is about approval, not money).
									const authorizedCents =
										period.active_amount_cents != null
											? period.active_amount_cents
											: amountCents;
									const financial = periodFinancialState(
										authorizedCents / 100,
										period.forecast,
									);

									const tone = financial ? utilizationTone(financial.usedPct) : "neutral";

									// items-baseline, not the grid default: the utilization cell is two lines, so
									// stretched cells centre themselves against it and the amounts drift apart.
									// Every cell's first line now sits on one baseline, and the second line reads
									// as the annotation it is.
									const rowClass = `grid ${gridCols} gap-2 px-1 py-3 items-baseline border-b border-border transition-colors duration-100 cursor-pointer group ${
										isZero ? "bg-surface-2" : "hover:bg-surface-2"
									}`;

									return (
										<div
											key={periodId}
											className={rowClass}
											onClick={() => handleRowClick(period)}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													handleRowClick(period);
												}
											}}
											role="button"
											tabIndex={0}
										>
											<span
												className={`text-sm font-medium ${isZero ? "text-text-3" : "text-text"}`}
											>
												{period.label ?? period.periodKey}
											</span>
											{/* Authorized and Forecast are read as a pair, so they share a typeface;
											    tabular-nums still keeps the digits in column. */}
											<div className="flex items-baseline gap-2">
												<span
													className={`text-sm tabular-nums ${isZero ? "text-text-3" : "text-text"}`}
												>
													{period.active_amount_cents != null ? (
														formatMoney(period.active_amount_cents, currency)
													) : amountCents > 0 ? (
														formatMoney(amountCents, currency)
													) : (
														<span className="text-text-3">&mdash;</span>
													)}
												</span>
												{period.derivedStatus === "draft_change" && (
													<span className="text-xs text-primary font-medium">
														Draft change
													</span>
												)}
											</div>
											{/* Projected total for the period. formatMoney (not toLocaleString) so it
											    carries the currency and matches the Authorized column beside it; the
											    title says which basis produced it, since an "actual" basis under a
											    column headed Forecast is actuals-to-date plus the remaining forecast. */}
											<span
												className="text-sm tabular-nums text-text-2"
												title={forecastTitle(period.forecastBasis)}
											>
												{period.forecast != null
													? formatMoney(Math.round(period.forecast * 100), currency)
													: "-"}
											</span>
											{/* Utilization — one comparable number per row, coloured only when the
											    figure has something to say. A word on every row ("Near cap" five
											    times down a column) is texture, not signal, so prose is reserved
											    for the one true exception: over the authorized amount. */}
											<span className="text-sm">
												{financial ? (
													<>
														<span
															className={`tabular-nums ${
																tone === "over"
																	? "text-danger font-medium"
																	: tone === "attention"
																		? "text-warning"
																		: tone === "watch"
																			? "text-text font-medium"
																			: "text-text"
															}`}
														>
															{financial.usedPct}%
														</span>
														<span
															className={`block text-xs tabular-nums ${
																tone === "over" ? "text-danger" : "text-text-3"
															}`}
														>
															{formatMoney(
																Math.round(Math.abs(financial.delta) * 100),
																currency,
															)}{" "}
															{tone === "over" ? "over" : "headroom"}
														</span>
													</>
												) : (
													<span className="text-text-3">&mdash;</span>
												)}
											</span>
											{showStatusColumn && (
												<span className="text-sm">
													{/* Phase 3: show derived period status badge if available */}
													{period.derivedStatus ? (
														<StatusPill
															status={
																period.derivedStatus === "draft_change"
																	? "draft"
																	: period.derivedStatus === "not_set"
																		? "archived"
																		: period.derivedStatus === "rejected" && period.active_amount_cents != null
																			? "active"
																			: period.derivedStatus
															}
														/>
													) : period.status === "breach" ? (
														<span className="inline-flex items-center gap-1 text-danger">
															<FontAwesomeIcon
																icon={icon({
																	name: "triangle-exclamation",
																	style: "solid",
																})}
																className="text-xs"
															/>
															Breach
														</span>
													) : (
														<StatusPill status={period.status || "draft"} />
													)}
												</span>
											)}
											{/* An icon has no text baseline worth aligning to — centre it in the row. */}
											<span className="flex items-center justify-end self-center opacity-0 group-hover:opacity-100 transition-opacity">
												<FontAwesomeIcon
													icon={icon({
														name: "pen-to-square",
														style: "regular",
													})}
													className="text-xs text-text-3"
												/>
											</span>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</PanelSection>
			)}
		</Panel>
	);
};

BudgetPeriodsPanel.propTypes = {
	periods: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			periodKey: PropTypes.string,
			label: PropTypes.string,
			amount: PropTypes.number,
			amountCents: PropTypes.number,
			startDate: PropTypes.string,
			endDate: PropTypes.string,
			status: PropTypes.string,
			forecast: PropTypes.number,
			derivedStatus: PropTypes.string, // Phase 3: "active"|"pending_approval"|"draft_change"|"not_set"
			active_amount_cents: PropTypes.number, // Phase 3: backend active amount
			period_id: PropTypes.number, // Phase 3: backend period ID for drawer
		}),
	).isRequired,
	onPeriodsChange: PropTypes.func.isRequired,
	periodType: PropTypes.string,
	totalAmountCents: PropTypes.number,
	allocationStrategy: PropTypes.string,
	onTotalChange: PropTypes.func,
	onStrategyChange: PropTypes.func,
	currency: PropTypes.string,
	onCurrencyChange: PropTypes.func,
	regeneratePeriodAmounts: PropTypes.func,
	onEditPeriod: PropTypes.func,
};

export default BudgetPeriodsPanel;
